import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  parseAddresses,
  usePracticeProfileQuery,
  useUpdatePracticeProfileMutation,
  type TenantAddress,
} from "../../apollo/practice";

/**
 * Contact info settings -- the phone, email, and office addresses shown on
 * the public website (footer contact section, contact surfaces). Lives under
 * Practice; the chrome editor's footer contact accordion links here.
 */
export function ContactInfoPage() {
  const { data, loading, error } = usePracticeProfileQuery();
  const [updateProfile, { loading: saving, error: saveError }] =
    useUpdatePracticeProfileMutation();

  const tenant = data?.currentTenant;
  const savedPhone = tenant?.phone ?? "";
  const savedEmail = tenant?.email ?? "";
  const savedAddresses = tenant?.addresses ?? null;

  const [phone, setPhone] = useState(savedPhone);
  const [email, setEmail] = useState(savedEmail);
  const [addresses, setAddresses] = useState<TenantAddress[]>([]);
  const [saved, setSaved] = useState(false);

  // Sync local state when the saved values arrive/change elsewhere
  useEffect(() => {
    setPhone(savedPhone);
  }, [savedPhone]);
  useEffect(() => {
    setEmail(savedEmail);
  }, [savedEmail]);
  useEffect(() => {
    setAddresses(parseAddresses(savedAddresses));
  }, [savedAddresses]);

  const dirty =
    phone !== savedPhone ||
    email !== savedEmail ||
    JSON.stringify(addresses) !== JSON.stringify(parseAddresses(savedAddresses));

  const updateAddress = (index: number, patch: Partial<TenantAddress>) => {
    setAddresses((prev) =>
      prev.map((a, i) => (i === index ? { ...a, ...patch } : a)),
    );
  };

  const addAddress = () => {
    setAddresses((prev) => [
      ...prev,
      { label: "", street: "", city: "", state: "", zip: "" },
    ]);
  };

  const removeAddress = (index: number) => {
    setAddresses((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    // Drop fully-empty address rows rather than persisting blanks
    const cleaned = addresses.filter((a) =>
      [a.label, a.street, a.city, a.state, a.zip].some((v) => v?.trim()),
    );
    void updateProfile({
      variables: {
        input: {
          phone: phone.trim(),
          email: email.trim(),
          addresses: JSON.stringify(cleaned),
        },
      },
    }).then(({ data: result }) => {
      if (result?.updateTenantConfig) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    });
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-[13px] text-ink-5">Loading contact info…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-rust-s/40 px-3 py-2 text-[12.5px] text-rust">
        Failed to load contact info: {error.message}
      </div>
    );
  }

  const fieldClass =
    "hair w-full rounded-md border bg-paper px-3 py-2 text-[13px] text-ink placeholder:text-ink-5 focus:border-tide focus:outline-none";
  const labelClass =
    "mb-1.5 block text-[11.5px] font-medium uppercase tracking-wider text-ink-4";

  return (
    <div className="grid grid-cols-12 gap-3">
      <section className="col-span-12 lg:col-span-8">
        <div className="mb-8">
          <h1 className="font-display text-[40px] font-medium leading-[1.05] tracking-tight">
            Contact info
          </h1>
          <p className="mt-2 max-w-xl text-[14.5px] leading-relaxed text-ink-3">
            The phone number, email, and office addresses shown on your public
            website — including the footer's contact section.
          </p>
        </div>

        <div className="space-y-8">
          {/* Phone + email */}
          <section className="shadow-card rounded-lg bg-paper-hi">
            <div className="hair-b border-b px-7 py-4">
              <h2 className="text-[14px] font-semibold text-ink">
                How clients reach you
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4 p-7 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="contact-phone">
                  Phone
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 555-0100"
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="contact-email">
                  Email
                </label>
                <input
                  id="contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="hello@yourpractice.com"
                  className={fieldClass}
                />
              </div>
            </div>
          </section>

          {/* Addresses */}
          <section className="shadow-card rounded-lg bg-paper-hi">
            <div className="hair-b border-b flex items-center justify-between px-7 py-4">
              <h2 className="text-[14px] font-semibold text-ink">
                Office addresses
              </h2>
              <span className="text-[11.5px] text-ink-5">
                Shown in the footer contact section
              </span>
            </div>
            <div className="space-y-4 p-7">
              {addresses.length === 0 && (
                <p className="text-[13px] text-ink-4">
                  No addresses yet. Add one to show it on your website.
                </p>
              )}
              {addresses.map((addr, i) => (
                <div
                  key={i}
                  className="hair relative rounded-md border bg-paper p-4"
                >
                  <button
                    type="button"
                    onClick={() => removeAddress(i)}
                    aria-label="Remove address"
                    className="absolute right-3 top-3 text-ink-5 transition-colors hover:text-rust"
                  >
                    <Trash2 size={14} strokeWidth={1.8} />
                  </button>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2 sm:max-w-[calc(50%-0.375rem)]">
                      <label className={labelClass}>Label</label>
                      <input
                        type="text"
                        value={addr.label ?? ""}
                        onChange={(e) =>
                          updateAddress(i, { label: e.target.value })
                        }
                        placeholder="Main Office"
                        className={fieldClass}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelClass}>Street</label>
                      <input
                        type="text"
                        value={addr.street ?? ""}
                        onChange={(e) =>
                          updateAddress(i, { street: e.target.value })
                        }
                        placeholder="223 W Main St"
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>City</label>
                      <input
                        type="text"
                        value={addr.city ?? ""}
                        onChange={(e) =>
                          updateAddress(i, { city: e.target.value })
                        }
                        placeholder="Los Gatos"
                        className={fieldClass}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>State</label>
                        <input
                          type="text"
                          value={addr.state ?? ""}
                          onChange={(e) =>
                            updateAddress(i, { state: e.target.value })
                          }
                          placeholder="CA"
                          className={fieldClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>ZIP</label>
                        <input
                          type="text"
                          value={addr.zip ?? ""}
                          onChange={(e) =>
                            updateAddress(i, { zip: e.target.value })
                          }
                          placeholder="95030"
                          className={fieldClass}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addAddress}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-tide hover:underline"
              >
                <Plus size={13} strokeWidth={2.2} />
                Add address
              </button>
            </div>
          </section>

          {/* Save bar */}
          <div className="flex items-center justify-end gap-3">
            {saveError && (
              <span className="text-[11.5px] text-rust">{saveError.message}</span>
            )}
            {saved && <span className="text-[11.5px] text-sage">Saved</span>}
            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || saving}
              className={`rounded-md px-4 py-1.5 text-[12.5px] font-medium ${
                dirty && !saving
                  ? "bg-ink text-paper-hi hover:bg-ink-2"
                  : "hair border bg-paper text-ink-5"
              }`}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
